import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useActivationsDashboard from '../components/activations/useActivationsDashboard';
import DashboardFilters from '../components/activations/DashboardFilters';
import KpiCards from '../components/activations/KpiCards';
import ActivationsTrendChart from '../components/activations/ActivationsTrendChart';
import ActivationMethodCard from '../components/activations/ActivationMethodCard';
import TopCustomersCard from '../components/activations/TopCustomersCard';
import ProductBreakdownCard from '../components/activations/ProductBreakdownCard';
import RecentActivationsTable from '../components/activations/RecentActivationsTable';
import RecentDeactivationsTable from '../components/activations/RecentDeactivationsTable';
import WatchListTable from '../components/activations/WatchListTable';
import { fmtShortDate, fromIsoDate, toIsoDate } from '../components/activations/formatters';

/** Turns the selected range into inclusive from/to dates (local time, "YYYY-MM-DD"). */
function resolveRange({ range, customFrom, customTo }) {
  if (range === 'custom') return { from: customFrom, to: customTo };
  const to = new Date();
  const from = new Date(to);
  if (range === '7d') from.setDate(from.getDate() - 6);
  else if (range === '90d') from.setDate(from.getDate() - 89);
  else if (range === '12m') { from.setFullYear(from.getFullYear() - 1); from.setDate(from.getDate() + 1); }
  else from.setDate(from.getDate() - 29); // 30d
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

export default function ActivationsDashboard() {
  const today = toIsoDate(new Date());
  const monthAgo = toIsoDate(new Date(Date.now() - 29 * 86400000));

  const [filters, setFilters] = useState({
    range: '30d',
    customFrom: monthAgo,
    customTo: today,
    product: '',
    customer: '',
    customerName: '',
    excludeTest: true,
  });
  const updateFilters = (patch) =>
    setFilters((f) => ({ ...f, ...patch, ...(patch.customer !== undefined && patch.customerName === undefined ? { customerName: '' } : {}) }));

  const { from, to } = useMemo(() => resolveRange(filters), [filters]);
  const { data, loading, error, reload } = useActivationsDashboard({
    from,
    to,
    product: filters.product,
    customer: filters.customer,
    excludeTest: filters.excludeTest,
  });

  // Product dropdown choices come from the unfiltered product breakdown, so they don't
  // collapse to a single entry once a product is picked.
  const [productOptions, setProductOptions] = useState([]);
  useEffect(() => {
    if (data && !data.filters.product) {
      const names = data.products.map((p) => p.product);
      setProductOptions((prev) => Array.from(new Set([...prev, ...names])).sort());
    }
  }, [data]);

  const periodLabel = from && to ? `${fmtShortDate(from)} – ${fmtShortDate(to)}, ${fromIsoDate(to).getFullYear()}` : '';

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontSize: 28 }}>Activations overview</Typography>
          <Typography variant="body2" color="text.secondary">
            {periodLabel} · compared with the previous period of the same length · times in server time
          </Typography>
        </Box>
        <DashboardFilters filters={filters} productOptions={productOptions} onChange={updateFilters} />
      </Stack>

      {(filters.customer || filters.product) && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {filters.product && (
            <Chip label={`Product: ${filters.product}`} onDelete={() => updateFilters({ product: '' })} />
          )}
          {filters.customer && (
            <Chip
              label={`Customer: ${filters.customerName || filters.customer}`}
              onDelete={() => updateFilters({ customer: '', customerName: '' })}
            />
          )}
        </Stack>
      )}

      {/* Thin bar while refreshing; reserves its height so the page doesn't jump. */}
      <Box sx={{ height: 4, mt: -2.5, mb: -0.5 }}>{loading && <LinearProgress />}</Box>

      {error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          <KpiCards kpis={data.kpis} />

          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
            <ActivationsTrendChart series={data.series} bucket={data.range.bucket} sx={{ flex: '999 1 600px' }} />
            <ActivationMethodCard methods={data.methods} failures={data.failures} sx={{ flex: '1 1 340px' }} />
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 520px), 1fr))', gap: 2 }}>
            <TopCustomersCard
              customers={data.topCustomers}
              onSelectCustomer={(c) => updateFilters({ customer: c.customerId ?? '', customerName: c.company })}
            />
            <ProductBreakdownCard products={data.products} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 560px), 1fr))', gap: 2 }}>
            <RecentActivationsTable rows={data.recentActivations} />
            <RecentDeactivationsTable rows={data.recentDeactivations} />
          </Box>

          <WatchListTable rows={data.watchList} />
        </Box>
      )}
    </Box>
  );
}
